import localforage from 'localforage';
import { cloneDeep, get as loGet, set as loSet } from 'lodash';
import moment from 'moment';
import PropTypes from 'prop-types';
import React, { useContext, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import useStateRef from 'react-usestateref';

import { RequestOffer } from 'connect/saga/handlers/offer/request-offer';
import { RequestReferralPromotion } from 'connect/saga/handlers/offer/request-referral-promotion';

import { UpdatePatientSilently } from 'connect/saga/handlers/update-patient-silently/update-patient-silently';
import { selectDataPatient, selectDataUser } from 'connect/selectors';
import Logger from 'utils/Logger';

import { STEP_KEY } from '../utils';

import {
  mapToMatch,
  METADATA_PROPERTIES,
  PATIENT_FUNNEL,
  PATIENT_FUNNEL_CONFIG,
  PATIENT_FUNNEL_LP_EXPERIMENT_DATA,
  PATIENT_FUNNEL_MARKETING,
  PATIENT_FUNNEL_PREFILL_DATA,
  PATIENT_FUNNEL_STEPS,
} from './constants';

export const FunnelContext = React.createContext();

export const FunnelProvider = ({ children }) => {
  const dispatch = useDispatch();

  let storage = null;
  let savedMarketing = null;
  let savedLpExperimentData = null;

  const [config, setConfig] = useState(null);
  const [configBranch, setConfigBranch] = useState(null);
  const [lpExperimentData, setLPExperimentData] = useState(null);
  const [progress, setProgress, progressRef] = useStateRef(null);
  const [marketing, setMarketing] = useState(null);
  const [prefillData, setPrefillData] = useState({});
  const [loading, setLoading] = useState(true);

  const referralPromotion = useSelector(
    RequestReferralPromotion.value.selector,
  );
  const offer = useSelector(RequestOffer.value.selector);
  const user = useSelector(selectDataUser);
  const patient = useSelector(selectDataPatient);

  // Fetch from indexedDb and store for use
  useEffect(() => {
    async function getStorage() {
      storage = await localforage.getItem(PATIENT_FUNNEL);
      const savedConfig = await localforage.getItem(PATIENT_FUNNEL_CONFIG);
      savedMarketing = await localforage.getItem(PATIENT_FUNNEL_MARKETING);
      const savedPrefillData = await localforage.getItem(
        PATIENT_FUNNEL_PREFILL_DATA,
      );
      savedLpExperimentData = await localforage.getItem(
        PATIENT_FUNNEL_LP_EXPERIMENT_DATA,
      );

      setProgress(storage || {});
      setConfig(savedConfig || {});
      setMarketing(savedMarketing || {});
      setPrefillData(savedPrefillData || {});
      setLPExperimentData(savedLpExperimentData || {});
      setLoading(false);
    }

    getStorage();
  }, []);

  // Store referral promotion id once we get it
  useEffect(() => {
    if (
      !loading
      && loGet(referralPromotion, 'id')
      && referralPromotion.id !== loGet(marketing, 'dmProps.referral_promo_id')
    ) {
      storeMarketingParameters();
    }
  }, [referralPromotion, loading, savedMarketing]);

  useEffect(() => {
    if (!patient?.id || patient?.startedSearchAt) {
      return;
    }
    dispatch(
      UpdatePatientSilently.createAction({
        patientId: patient.id,
        startedSearchAt: moment.utc().toISOString(),
      }),
    );
  }, [patient]);

  /**
   * Saves the data for the given funnel step.
   *
   * @param stepKey - step key (e.g. STEP_KEY.LOCATION)
   * @param data - step data object.
   * @param isComplete - whether the step is complete.
   * @param skipSteps - array of further steps to skip. Can be defined as array of step names (e.g. [STEP_KEY.LOCATION]) or array of steps with data (e.g. [{ stepName: STEP_KEY.LOCATION, data: { ... } }])
   */
  const save = (stepKey, data, isComplete, skipSteps) => {
    if (stepKey === STEP_KEY.LOCATION && data && !data.marketName) {
      Logger.error(
        'Location step was updated without market name',
        new Error(data),
      );
    }

    setProgress(currentProgress => {
      const progressCopy = cloneDeep(currentProgress);

      // Every time we partially save a step (!isComplete), we want to increase this count by one.
      // Helps us track sub-steps.
      const baseCount = loGet(
        loGet(progressCopy, `[${stepKey}]`),
        `metadata[${METADATA_PROPERTIES.COUNT}]`,
        0,
      );

      loSet(progressCopy, stepKey, {
        id: stepKey,
        isComplete: isComplete || false,
        data,
        metadata: {
          [METADATA_PROPERTIES.COUNT]: !isComplete ? baseCount + 1 : baseCount,
        },
      });

      (skipSteps || []).forEach(skipStep => {
        const skipStepName =
          typeof skipStep === 'string' ? skipStep : skipStep?.stepName;

        if (skipStepName) {
          loSet(progressCopy, skipStepName, {
            id: skipStepName,
            isComplete: true,
            data: skipStep.data,
            metadata: {
              [METADATA_PROPERTIES.SKIPPED]: true,
            },
          });
        }
      });

      localforage.setItem(PATIENT_FUNNEL, progressCopy);
      return progressCopy;
    });
  };

  const saveConfigSteps = configuration => {
    const steps = configuration.order.map(({ name, path }) => ({
      name,
      path,
    }));
    localforage.setItem(PATIENT_FUNNEL_STEPS, steps);
  };
  const saveConfig = ({
    id,
    branchName,
    subId,
    test,
    variant,
    unselectedControlConfigs,
  } = {}) => {
    const updatedConfig = {
      id,
      branchName,
      subId,
      test,
      variant,
      ...unselectedControlConfigs && { unselectedControlConfigs },
    };

    setConfig(updatedConfig);
    localforage.setItem(PATIENT_FUNNEL_CONFIG, updatedConfig);
  };

  const saveConfigBranch = newBranch => {
    setConfigBranch(newBranch);
    saveConfig({ ...config, branchName: newBranch });
  };

  const getConfigBranch = () => configBranch;

  const saveLPExperiment = (experimentName, experimentVariant) => {
    const experimentData = {
      experiment_name: experimentName,
      experiment_variant: experimentVariant,
    };

    setLPExperimentData(experimentData);
    localforage.setItem(PATIENT_FUNNEL_LP_EXPERIMENT_DATA, experimentData);
  };

  /**
   * Get the data for the given funnel step.
   *
   * @param stepKey - step key (e.g. STEP_KEY.LOCATION).
   * @param prop - optional property to pull from the step instead of data.
   */
  const get = (stepKey, prop = null) => {
    // `setProgress(progressCopy)` in the `save` function is asynchronous,
    // so when we need to operate with the funnel data right after saving it,
    // we need to get the data from the ref instead of the state.
    // This is a workaround to avoid getting the old data.
    const step = loGet(progressRef.current, stepKey, {});

    return prop ? step[prop] : step.data;
  };

  // This is so we can mock this function in tests
  const getActiveConfig = () => config;

  const storeMarketingParameters = async (
    utmSource,
    utmCampaign,
    utmContent,
    utmMedium,
    utmTerm,
    unbounceVariant,
    referralCode,
    referralPromo,
    offerCode,
  ) => {
    savedMarketing = await localforage.getItem(PATIENT_FUNNEL_MARKETING);

    let formattedParameters = {};

    if (referralCode || referralPromo || offerCode) {
      formattedParameters = {
        utmTags: {
          source: utmSource,
          campaign: utmCampaign,
          content: utmContent,
          medium: utmMedium,
          term: utmTerm,
        },
        unbounceProps: {
          variant: unbounceVariant,
        },
        dmProps: {
          referral_code: referralCode,
          referral_promo: referralPromo || undefined,
        },
      };
    } else {
      formattedParameters = {
        utmTags: {
          source: loGet(savedMarketing, 'utmTags.source'),
          campaign: loGet(savedMarketing, 'utmTags.campaign'),
          content: loGet(savedMarketing, 'utmTags.content'),
          medium: loGet(savedMarketing, 'utmTags.medium'),
          term: loGet(savedMarketing, 'utmTags.term'),
        },
        unbounceProps: {
          variant: get(savedMarketing, 'unbounceProps.variant'),
        },
        dmProps: {
          referral_code: loGet(savedMarketing, 'dmProps.referral_code'),
          referral_promo: loGet(savedMarketing, 'dmProps.referral_promo')
            ? savedMarketing.dmProps.referral_promo
            : undefined,
          referral_promo_id: loGet(referralPromotion, 'id'),
        },
      };
    }

    setMarketing(formattedParameters);
    localforage.setItem(PATIENT_FUNNEL_MARKETING, formattedParameters);
  };

  const storePrefillData = async prefillDataToStore => {
    setPrefillData(currentPrefillData => {
      const newPrefillData = {
        ...currentPrefillData,
        ...prefillDataToStore,
      };

      localforage.setItem(PATIENT_FUNNEL_PREFILL_DATA, newPrefillData);
      return newPrefillData;
    });
  };

  const getPrefillData = () => prefillData;

  // Clears both local & cached data (immediate clear)
  const clear = async () => {
    saveConfig();
    saveConfigSteps({ order: [] });
    setProgress({});
    await clearCache();
  };

  const fillAnswersFromPrefillData = () => {
    if (!prefillData) {
      return;
    }

    Object.entries(prefillData).forEach(([stepKey, prefillValue]) => {
      save(stepKey, prefillValue, true);
    });
  };

  // Clears cache only (cleared on reload)
  const clearCache = async () => {
    await localforage.setItem(PATIENT_FUNNEL, {});
  };

  // Clears cache only (cleared on reload)
  const clearMarketingCache = () => {
    localforage.setItem(PATIENT_FUNNEL_MARKETING, {});
  };

  const clearPrefillData = () => {
    localforage.setItem(PATIENT_FUNNEL_PREFILL_DATA, {});
  };

  const isEmpty = ({ excludedSteps = [] } = {}) => {
    if (loading) {
      return false;
    }

    const progressMinusExcludedSteps = Object.keys(progress || {}).filter(
      stepName => !excludedSteps.includes(stepName),
    );

    return progressMinusExcludedSteps.length === 0;
  };

  const transform = () =>
    mapToMatch(offer, referralPromotion, user, marketing, progress);

  return (
    <FunnelContext.Provider
      value={{
        loading,
        config,
        progress,
        prefillData,
        lpExperimentData,
        save,
        saveConfig,
        saveConfigSteps,
        getActiveConfig,
        saveConfigBranch,
        getConfigBranch,
        saveLPExperiment,
        get,
        storeMarketingParameters,
        storePrefillData,
        getPrefillData,
        fillAnswersFromPrefillData,
        clearPrefillData,
        clear,
        clearCache,
        clearMarketingCache,
        isEmpty,
        transform,
      }}
    >
      {children}
    </FunnelContext.Provider>
  );
};

FunnelProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export const useFunnelStorage = () => useContext(FunnelContext);
